import React, { useCallback, useState } from 'react';
import {
	SUBSCRIPTION_LOAD_FAIL,
	SUBSCRIPTION_EXPIRES,
	SUBSCRIPTION_CANCELLED_ODIDO_GOGOGODEALS,
	SUBSCRIPTION_CANCEL_CANCELLED,
	SUBSCRIPTION_CANCELLED_FREE,
	SUBSCRIPTION_DISCOUNT_PERIOD_DURATION,
	SUBSCRIPTION_PROMO_PERIOD_DURATION,
	NOTIFICATION_HIDE_BUTTON,
	SUBSCRIPTION_PROMO_PERIOD_DURATION_BLACK_FRIDAY,
	SUBSCRIPTION_STOPS_TERMED_SUSPENDED,
} from '../../../../constants/Copy';
import ResellerCopy from '../../subscription/reseller/ResellerCopy';
import { friendlyDate, addDays } from '../../../../helpers/datetime';
import { formatPrice } from '../../../../helpers/number';
import './RenderTierInfo.scss';
import TermedCopy from '../../subscription/termed/TermedCopy';
import { CANCELLATION_BLOCKED_REASON } from '../../../../constants/CancelAccount';
import { cancellationStatuses } from '../../../../constants/GraphQL';
import { RESELLER_IDS } from '../../../../constants/ResellersAndPartners';
import Message from '../../../common/message/Message';
import { ACTIVE_PLAN_PERIOD } from '../../../../constants/Plans';
import Notification, { NOTIFICATION_TYPE_SUCCESS } from '../../../common/notification/Notification';
import { getSubscriptionEndDate } from '../../../../helpers/subscription';
import { isOnFreeTier } from '../../../../helpers/tiers';
import { useSelector } from 'react-redux';

const TermedSuspended = ({ subscription }) => (
	<BlockedSwitchInfoWrapper>
		<p>
			{SUBSCRIPTION_STOPS_TERMED_SUSPENDED.replace(
				'{end_date}',
				friendlyDate(subscription.endDate)
			)}
		</p>
	</BlockedSwitchInfoWrapper>
);

const BlockedSwitchInfoWrapper = ({ children }) => {
	return (
		<div className="profile__tier-details-tierchange-pending" data-test="tierchange-pending">
			{children}
		</div>
	);
};

function renderNoSubscription() {
	/* eslint-disable react/no-danger */
	return (
		<BlockedSwitchInfoWrapper>
			<p
				className="profile__subscription-details-text"
				dangerouslySetInnerHTML={{ __html: SUBSCRIPTION_LOAD_FAIL }}
			/>
		</BlockedSwitchInfoWrapper>
	);
}

function renderReseller({ activeSubscription, user, showRenderReseller, setShowRenderReseller }) {
	const { trustedParty } = activeSubscription;

	if (!showRenderReseller || (trustedParty && trustedParty.trim() !== '')) {
		return null;
	}

	return (
		<Notification
			title={<ResellerCopy subscription={activeSubscription} user={user} />}
			type={NOTIFICATION_TYPE_SUCCESS}
			visible
			button={{
				onClick: () => setShowRenderReseller(false),
				text: NOTIFICATION_HIDE_BUTTON,
			}}
		/>
	);
}

function renderTermed(subscription) {
	return (
		<BlockedSwitchInfoWrapper>
			<TermedCopy subscription={subscription} />
		</BlockedSwitchInfoWrapper>
	);
}

function renderCancellationCopy(subscriptionEndDate, currentPlan) {
	const cancellationCopy = isOnFreeTier(currentPlan.planDescription.tierId)
		? SUBSCRIPTION_CANCELLED_FREE.replace('{end_date}', subscriptionEndDate)
		: currentPlan.reseller === RESELLER_IDS.TMOBILE_GOGOGODEALS
		? SUBSCRIPTION_CANCELLED_ODIDO_GOGOGODEALS.replace('{end_date}', subscriptionEndDate)
		: `${SUBSCRIPTION_CANCEL_CANCELLED} ${SUBSCRIPTION_EXPIRES} ${subscriptionEndDate}.`;

	return (
		<BlockedSwitchInfoWrapper>
			<p dangerouslySetInnerHTML={{ __html: cancellationCopy }} />
		</BlockedSwitchInfoWrapper>
	);
}

function renderInformation({
	currentPlan,
	isCancelled,
	isCancellationBlocked,
	activeSubscription,
	user,
	showRenderReseller,
	setShowRenderReseller,
	isStitched,
}) {
	const cancellationBlockedReason =
		isCancellationBlocked && activeSubscription.allowsCancellation.reason;
	if (currentPlan.activePlanPeriod === ACTIVE_PLAN_PERIOD.DISCOUNTED) {
		return renderSubscriptionEnd(currentPlan);
	}
	if (isCancelled) return null;
	if (cancellationBlockedReason === CANCELLATION_BLOCKED_REASON.IS_TRUSTED_PARTY_SUBSCRIPTION)
		return renderReseller({ activeSubscription, user, showRenderReseller, setShowRenderReseller });
	if (
		cancellationBlockedReason === CANCELLATION_BLOCKED_REASON.IS_TERMED_SUBSCRIPTION &&
		!isStitched
	)
		return renderTermed(activeSubscription);

	return null;
}

function renderOnePeriod(endDate) {
	return (
		<BlockedSwitchInfoWrapper>
			{SUBSCRIPTION_EXPIRES} {friendlyDate(endDate)}.
		</BlockedSwitchInfoWrapper>
	);
}

function renderSubscriptionEnd(currentPlan) {
	return (
		<BlockedSwitchInfoWrapper>
			{currentPlan.activePlanPeriod === ACTIVE_PLAN_PERIOD.DISCOUNTED && (
				<Message
					message={SUBSCRIPTION_DISCOUNT_PERIOD_DURATION}
					values={{
						date: friendlyDate(addDays(currentPlan.planDescription.normalPeriod.startDate, -1)),
						price: formatPrice(currentPlan.planDescription.normalPeriod.price),
					}}
				/>
			)}
			{currentPlan.activePlanPeriod === ACTIVE_PLAN_PERIOD.PROMO && (
				<Message
					message={
						currentPlan.planDescription.planId === 'Basis2WeekPromo6M2EurDiscount' ||
						currentPlan.planDescription.planId === 'Plus2WeekPromo6M2EurDiscount'
							? SUBSCRIPTION_PROMO_PERIOD_DURATION_BLACK_FRIDAY
							: SUBSCRIPTION_PROMO_PERIOD_DURATION
					}
					values={
						currentPlan.planDescription.discountedPeriod
							? {
									date: friendlyDate(
										addDays(currentPlan.planDescription.discountedPeriod.startDate, -1)
									),
									price: formatPrice(currentPlan.planDescription.discountedPeriod.price),
							  }
							: {
									date: friendlyDate(
										addDays(currentPlan.planDescription.normalPeriod.startDate, -1)
									),
									price: formatPrice(currentPlan.planDescription.normalPeriod.price),
							  }
					}
				/>
			)}
		</BlockedSwitchInfoWrapper>
	);
}

const RenderTierInfo = ({
	isActiveNPLSubscription,
	isConvertedToTermedUser,
	activeSubscription,
	nextBillingDate,
	currentPlan,
	isCancelled,
	isLoading,
	isStitched,
}) => {
	const [showRenderReseller, setShowRenderReseller] = useState(true);
	const hasSuspendedSubscription = useSelector(
		(state) => state.subscription.hasSuspendedSubscription
	);
	const renderInfo = useCallback(() => {
		const user = {
			isConvertedToTermedUser,
			isActiveNPLSubscription,
		};
		const isCancellationBlocked =
			activeSubscription?.allowsCancellation?.__typename ===
			cancellationStatuses.CANCELLATION_BLOCKED;
		const isDiscountedOrPromo =
			currentPlan.activePlanPeriod === ACTIVE_PLAN_PERIOD.DISCOUNTED ||
			currentPlan.activePlanPeriod === ACTIVE_PLAN_PERIOD.PROMO;

		let tierInfo = null;

		const subscriptionEndDate = getSubscriptionEndDate(
			activeSubscription,
			nextBillingDate,
			isCancelled
		);

		if (!activeSubscription) {
			tierInfo = renderNoSubscription();
		} else if (hasSuspendedSubscription && activeSubscription.termType === 'TERMED') {
			tierInfo = <TermedSuspended subscription={activeSubscription} />;
		} else if (isCancellationBlocked) {
			tierInfo = renderInformation({
				currentPlan,
				isCancelled,
				isCancellationBlocked,
				activeSubscription,
				user,
				nextBillingDate,
				showRenderReseller,
				setShowRenderReseller,
				isStitched,
			});
		} else if (isCancelled && subscriptionEndDate) {
			tierInfo = renderCancellationCopy(subscriptionEndDate, currentPlan);
		} else if (!isCancelled && subscriptionEndDate && isDiscountedOrPromo) {
			tierInfo = renderSubscriptionEnd(currentPlan);
		} else if (activeSubscription.endDate && activeSubscription.termType === 'TERMED') {
			tierInfo = renderOnePeriod(activeSubscription.endDate);
		}
		return tierInfo ? <div className="render-tier-info">{tierInfo}</div> : tierInfo;
	}, [
		isConvertedToTermedUser,
		isActiveNPLSubscription,
		activeSubscription,
		currentPlan,
		nextBillingDate,
		isCancelled,
		showRenderReseller,
		isStitched,
		hasSuspendedSubscription,
	]);
	return isLoading ? null : renderInfo();
};

export default RenderTierInfo;
